import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Divider, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, serverTimestamp, addDoc, collection, updateDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import logo from '../../images/logo.svg';
import { GoogleIcon, FacebookIcon } from '../../components/CustomIcons';
import { colors, typography, radius, inputSx, btnPrimary } from '../../theme';

const SignUpShop = () => {
  const navigate = useNavigate();
  const [loading,  setLoading]  = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [error,    setError]    = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const email = data.get('email'), password = data.get('password'), shopName = data.get('shopName'), barberName = data.get('barberName'), phone = data.get('phone'), address = data.get('address');
    if (!shopName || !barberName || !email || !phone || !password || !address) { setError('Please fill in all fields.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setError(''); setLoading(true);
    let user = null;
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      user = userCredential.user;
      const shopRef = await addDoc(collection(db, 'shops'), { type:'shop', isActive:true, shopName, barberName, phone, email, ownerId:user.uid, address, image:'', ratings:{average:0,totalReviews:0}, services:[{name:'Haircut',price:20,duration:30},{name:'Beard Trim',price:15,duration:20}], operatingHours:{monday:{open:'09:00',close:'18:00',isOff:false},tuesday:{open:'09:00',close:'18:00',isOff:false},wednesday:{open:'09:00',close:'18:00',isOff:false},thursday:{open:'09:00',close:'18:00',isOff:false},friday:{open:'09:00',close:'18:00',isOff:false},saturday:{open:'09:00',close:'14:00',isOff:false},sunday:{open:'00:00',close:'00:00',isOff:true}}, createdAt:serverTimestamp() });
      await updateDoc(shopRef, { qrCodeId: shopRef.id });
      const barberRef = await addDoc(collection(db, 'shops', shopRef.id, 'barbers'), { name:barberName, email, image:'', experience:0, specialty:[], rating:0, waitingList:[], isAvailable:true, userId:user.uid, createdAt:serverTimestamp() });
      await setDoc(doc(db, 'users', user.uid), { email, name:barberName, role:'shop_owner', shopId:shopRef.id, barberId:barberRef.id, createdAt:serverTimestamp() });
      navigate(`/user/${shopRef.id}/dashboard`);
    } catch (err) {
      if (user) { try { await user.delete(); } catch (_) {} }
      setError(err.code === 'auth/email-already-in-use' ? 'This email is already registered.' : err.message);
    }
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      const user = result.user;
      const userSnap = await getDoc(doc(db, 'users', user.uid));
      if (userSnap.exists()) {
        const userData = userSnap.data();
        if (userData.shopId) { navigate(`/user/${userData.shopId}/dashboard`); return; }
        else { navigate('/barber/setup'); return; }
      }
      await setDoc(doc(db, 'users', user.uid), { email:user.email, name:user.displayName, role:'shop_owner', shopId:null, barberId:null, createdAt:serverTimestamp() });
      navigate('/barber/setup');
    } catch (err) { console.error(err.message); setError('Google sign-in failed. Try again.'); }
    setLoading(false);
  };

  return (
    <Box sx={{ minHeight:'100vh', bgcolor:colors.bg, display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', px:2, py:4 }}>
      <Box sx={{ width:'100%', maxWidth:420 }}>
        <Box sx={{ display:'flex', justifyContent:'center', mb:3 }}><img src={logo} alt="DigitalQ" style={{ width:80, height:'auto' }} /></Box>
        <Box sx={{ bgcolor:colors.bgCard, border:`1px solid ${colors.border}`, borderRadius:radius.lg, p:3 }}>
          <Typography sx={{ fontFamily:typography.poppins, fontSize:22, fontWeight:700, color:colors.textPrimary, mb:0.5 }}>Register Your Shop</Typography>
          <Typography sx={{ fontSize:13, color:colors.textFaint, mb:3 }}>Create your barber shop account</Typography>
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ display:'flex', flexDirection:'column', gap:2 }}>
            <TextField name="shopName"   label="Shop Name"    required size="small" sx={inputSx} />
            <TextField name="barberName" label="Your Name"    required size="small" sx={inputSx} />
            <TextField name="address"    label="Shop Address" required size="small" sx={inputSx} />
            <TextField name="phone"      label="Phone Number" required size="small" sx={inputSx} />
            <TextField name="email"      label="Email"        required size="small" sx={inputSx} type="email" />
            <TextField name="password"   label="Password"     required size="small" sx={inputSx} type={showPass ? 'text' : 'password'}
              InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPass(s => !s)} edge="end" size="small" sx={{ color:colors.textFaintest }}>{showPass ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}</IconButton></InputAdornment> }} />
            {error && <Typography sx={{ fontSize:12, color:colors.error, textAlign:'center' }}>{error}</Typography>}
            <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ ...btnPrimary, fontSize:14, py:1.2 }}>
              {loading ? <CircularProgress size={18} sx={{ color:colors.bgDeep }} /> : 'Create Barber Shop Account'}
            </Button>
            <Typography sx={{ textAlign:'center', fontSize:13, color:colors.textFaint }}>
              Already have an account?{' '}
              <Typography component="span" onClick={() => navigate('/signin')} sx={{ color:colors.primary, cursor:'pointer', fontSize:13, '&:hover':{ textDecoration:'underline' } }}>Sign in</Typography>
            </Typography>
          </Box>
          <Divider sx={{ borderColor:colors.border, my:2.5 }}><Typography sx={{ fontSize:12, color:colors.textFaintest, px:1 }}>or</Typography></Divider>
          <Box sx={{ display:'flex', flexDirection:'column', gap:1.5 }}>
            <Button fullWidth variant="outlined" onClick={handleGoogleSignIn} disabled={loading} startIcon={<GoogleIcon />} sx={{ borderColor:colors.border, color:colors.textPrimary, textTransform:'none', fontSize:13, borderRadius:radius.md, py:1, '&:hover':{ borderColor:colors.primaryBorder, bgcolor:colors.bg } }}>Continue with Google</Button>
            <Button fullWidth variant="outlined" disabled startIcon={<FacebookIcon />} sx={{ borderColor:colors.border, color:colors.textFaintest, textTransform:'none', fontSize:13, borderRadius:radius.md, py:1 }}>Facebook (Coming Soon)</Button>
          </Box>
          <Box sx={{ mt:2.5, pt:2.5, borderTop:`1px solid ${colors.border}`, textAlign:'center' }}>
            <Typography sx={{ fontSize:13, color:colors.textFaint }}>
              Looking to book a haircut?{' '}
              <Typography component="span" onClick={() => navigate('/')} sx={{ color:colors.primary, cursor:'pointer', fontSize:13, '&:hover':{ textDecoration:'underline' } }}>Find a barber</Typography>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SignUpShop;